import { useState } from 'react'

import { Edit3, PlusCircle } from 'lucide-react'

import { cn } from '@fortem-monorepo/js-utilities'
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Separator,
} from '@fortem-monorepo/react-ui'
import { PnlAttributions } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { PERMISSIONS } from '@/constants/permissions'
import { AttributionTypeBadge } from '@/pages/pnl-attributions/components/AttributionTypeBadge'
import { usePnlContext } from '@/pages/pnl-attributions/context'
import { usePnlAttributionsActions } from '@/pages/pnl-attributions/pnl-attribution-actions'
import { formatNumber } from '@/utilities/format'

export interface TradeRowProps {
  trade: PnlAttributions.AttributionRow
}

export const TradeRow: React.FC<TradeRowProps> = ({ trade }) => {
  const [open, setOpen] = useState(true)

  const { inspectingBatchId, selectedBatchState } = usePnlContext()
  const { openAdjustTrade, openAddToTrade } = usePnlAttributionsActions()

  return (
    <Collapsible className={cn('border rounded-md overflow-hidden')} open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className={cn('flex flex-col gap-1 p-4 w-full bg-gray-50 hover:bg-gray-100 cursor-pointer', {
          'border-b bg-gray-100/75': open,
        })}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col items-start gap-1">
            <AttributionTypeBadge attribution_type={trade.attribution_type} />
            <span className="text-sm">{trade.unique_identifier}</span>
            <span className="text-xs text-muted-foreground">{trade.trade_id}</span>
          </div>
          <Badge variant={'secondary'} className={'border border-gray-300 bg-gray-200'}>
            {trade.trade_type}
          </Badge>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className={cn('p-4 space-y-8')}>
        {selectedBatchState?.state !== 'PUBLISHED' && (
          <CanAccess permissions={[PERMISSIONS.WRITE_PNL_VALIDATION]}>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={'outline'}
                onClick={() =>
                  openAdjustTrade({
                    original_pnl: trade.net_pnl,
                    trade_id: trade.trade_id,
                    attribution_id: trade.attribution_id,
                    batch_id: inspectingBatchId,
                  })
                }
              >
                Adjust Net P&L for trade
                <Edit3 />
              </Button>
              <Button
                size="sm"
                variant={'outline'}
                onClick={() =>
                  openAddToTrade({
                    trade_id: trade.trade_id,
                    attribution_id: trade.attribution_id,
                    batch_id: inspectingBatchId,
                  })
                }
              >
                Add P&L component for trade <PlusCircle />
              </Button>
            </div>
          </CanAccess>
        )}
        <div className="flex gap-8">
          <div className="flex-1">
            <span className="text-muted-foreground uppercase tracking-wide text-xs">P&L Summary</span>
            <Separator className="my-2" />
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <TableHeader align="left">Type</TableHeader>
                  <TableHeader align="right">Pnl</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell align="left">Gross</TableCell>
                  <TableCell align="right" className="font-mono">
                    {formatNumber(trade.gross_pnl)}
                  </TableCell>
                </tr>
                <tr>
                  <TableCell align="left">Net</TableCell>
                  <TableCell align="right" className="font-mono">
                    {formatNumber(trade.net_pnl)}
                  </TableCell>
                </tr>
                <tr>
                  <TableCell align="left">Market</TableCell>
                  <TableCell align="right" className="font-mono">
                    {formatNumber(trade.market_pnl)}
                  </TableCell>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex-2">
            <span className="text-muted-foreground uppercase tracking-wide text-xs">P&L Components</span>
            <Separator className="my-2" />
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <TableHeader align="left">Type</TableHeader>
                  <TableHeader align="right">Pnl</TableHeader>
                  <TableHeader align="center">Rule</TableHeader>
                </tr>
              </thead>
              <tbody>
                {trade.components.map((component) => {
                  return (
                    <tr key={component.component_id} className={cn('not-last:border-b')}>
                      <TableCell align="left">{component.component_type}</TableCell>
                      <TableCell align="right" className="font-mono">
                        {formatNumber(component.amount_base, {
                          decimalPlaces: 0,
                          roundingMode: 'round',
                        })}
                      </TableCell>
                      <TableCell align="center" className="text-xs">
                        {component.calculation_method}
                      </TableCell>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

export interface TableHeaderProps extends React.ComponentProps<'td'> {
  align?: 'left' | 'center' | 'right'
}

export const TableHeader: React.FC<TableHeaderProps> = ({ align = 'center', className, ...props }) => {
  return (
    <td
      {...props}
      className={cn(
        'first:pr-4 not-first:not-last:px-4 text-muted-foreground py-2 font-medium text-xs uppercase',
        {
          'text-left': align === 'left',
          'text-center': align === 'center',
          'text-right': align === 'right',
        },
        className,
      )}
    />
  )
}

export interface TableCellProps extends React.ComponentProps<'th'> {
  align?: 'left' | 'center' | 'right'
}

export const TableCell: React.FC<TableCellProps> = ({ align = 'center', className, ...props }) => {
  return (
    <th
      {...props}
      className={cn(
        'first:pr-4 not-first:not-last:px-4 last:pl-4 py-2 text-sm font-normal tracking-wide text-heading',
        {
          'text-left': align === 'left',
          'text-center': align === 'center',
          'text-right': align === 'right',
        },
        className,
      )}
    />
  )
}
